import React, { useEffect, useState } from 'react';

import { useUser } from '../../store';
import { useEvent } from '../../../virtual/store';

import { ChatConfig } from '../../../config/app/components/Chat';
import { useCtx } from '../../../config/hooks';
import objectHash from 'object-hash';
import _ from 'lodash';

export interface ChatConfigProps {
	text?: string;
}

export enum ChatType {
	TawkTo = 'tawk-to'
}

export interface ChatProps {}

const tawkToElementId = 'tawkId_main_script';

export interface ChatAttributes extends Object {
	[key: string]: any;
}

export const Chat: React.FC<ChatProps> = props => {
	const {
		config: [config]
	} = useCtx<ChatConfigProps>(ChatConfig);

	const { activeUser } = useUser();
	const { activeEvent } = useEvent();

	const [visible, setVisible] = useState(true);
	const [loaded, setLoaded] = useState(false);

	useEffect(() => {
		let chatType =
			(activeEvent?.properties as any)?.app?.virtual?.generalSettings
				?.chatSettings?.chatType || '';
		let chatId =
			(activeEvent?.properties as any)?.app?.virtual?.generalSettings
				?.chatSettings?.chatId || '';
		let chatKey =
			(activeEvent?.properties as any)?.app?.virtual?.generalSettings
				?.chatSettings?.chatKey || '';

		if (chatType === ChatType.TawkTo) {
			let tawkApi = tawkTo(
				chatId,
				chatKey,
				activeUser?.name,
				activeUser?.email
			);

			let attributes: ChatAttributes = _.pickBy(
				{
					name: activeUser?.name,
					email: activeUser?.email,
					userId: activeUser?.id,
					eventId: activeEvent?.id,
					event: activeEvent?.name
				},
				_.isString
			);

			let attributesHash = objectHash(attributes);

			// set Attributes imediately if already loaded and they have changed
			if ((window as WindowTawkTo).Tawk_Loaded === true) {
				if (!loaded) setLoaded(false);
				if (
					(window as WindowTawkTo).Tawk_AttributesHash !== attributesHash &&
					tawkApi &&
					tawkApi.setAttributes
				) {
					tawkApi.setAttributes(attributes, function (error?: Error) {
						if (error) {
							console.error(error);
							delete (window as WindowTawkTo).Tawk_AttributesHash;
						}
					});
					(window as WindowTawkTo).Tawk_AttributesHash = attributesHash;
				}
				// otherwise set them on load
			} else if (!tawkApi.onLoad) {
				tawkApi.onLoad = function () {
					tawkApi.setAttributes(attributes, function (error: Error) {
						if (error) {
							console.error(error);
							delete (window as WindowTawkTo).Tawk_AttributesHash;
						}
					});
					(window as WindowTawkTo).Tawk_AttributesHash = attributesHash;
					(window as WindowTawkTo).Tawk_Loaded = true;
					setLoaded(true);
				};
			} else {
				if (loaded) setLoaded(false);
			}

			if (loaded) {
				setVisible(true);
				if (tawkApi.showWidget) tawkApi.showWidget();
			}
		}
	}, [loaded, activeUser, activeEvent]);

	return <></>;
};

interface WindowTawkTo extends Window {
	Tawk_API?: any;
	Tawk_LoadStart?: Date;
	Tawk_Id?: string;
	Tawk_Loaded?: boolean;
	Tawk_AttributesHash?: string;
}

const tawkTo = (
	propertyId: string,
	clientKey: string,
	name?: string,
	email?: string
): any => {
	if (!window) {
		throw new Error('DOM is unavailable for Tawk.to');
	}

	let tawkId: string = objectHash({ propertyId, clientKey, name, email });

	let tawk = document.getElementById(
		tawkToElementId
	) as HTMLScriptElement | null;

	// if the tawk exists but the tawk identity is differnt. kill the tawk script element and remove the Tawk window settings.
	// change in propertyId, key or user name or email should cause reload
	if (
		!(window as WindowTawkTo).Tawk_API ||
		(tawk && (window as WindowTawkTo).Tawk_Id !== tawkId)
	) {
		if (tawk) {
			tawk.remove();
			tawk = null;
		}
		delete (window as WindowTawkTo).Tawk_API;
		delete (window as WindowTawkTo).Tawk_LoadStart;
		delete (window as WindowTawkTo).Tawk_Id;
		delete (window as WindowTawkTo).Tawk_Loaded;
		delete (window as WindowTawkTo).Tawk_AttributesHash;
	}

	// if tawk not loaded or was removed for reload then load it
	if (!tawk) {
		(window as WindowTawkTo).Tawk_API = {};
		(window as WindowTawkTo).Tawk_LoadStart = new Date();
		(window as WindowTawkTo).Tawk_Id = tawkId;
		(window as WindowTawkTo).Tawk_Loaded = false;

		const script = document.createElement('script');
		script.id = tawkToElementId;
		script.async = true;
		script.src = 'https://embed.tawk.to/' + propertyId + '/' + clientKey;
		script.setAttribute('crossorigin', '*');

		const first_script_tag = document.getElementsByTagName('script')[0];
		if (!first_script_tag || !first_script_tag.parentNode) {
			throw new Error('DOM is unavailable for Tawk.to');
		}

		first_script_tag.parentNode.insertBefore(script, first_script_tag);
	}

	// Prevent TawkTo to create root script if it already exists
	return (window as WindowTawkTo).Tawk_API;
};
